import InlineStatus from './InlineStatus';
import SetupTrustHelperBand from './SetupTrustHelperBand';
import {
  formatSourceProofOccurredAt,
} from '../../lib/sourceProof';
import {
  getSourceFreshnessAgeLabel,
  getSourceFreshnessHelper,
  getSourceFreshnessSummary,
  getSourceFreshnessTone,
  getSourceFreshnessVerdictLabel,
  type SourceFreshnessSnapshot,
} from '../../lib/sourceFreshnessHandoff';

interface CarriedSourceFreshnessRailProps {
  snapshot: SourceFreshnessSnapshot;
  decisionRoute: '/transactions' | '/goals' | '/rebalancing' | '/tax-optimisation';
  title?: string;
  className?: string;
  testId?: string;
}

function formatCount(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-US').format(value ?? 0);
}

export default function CarriedSourceFreshnessRail({
  snapshot,
  decisionRoute,
  title = 'Carried source freshness',
  className = '',
  testId,
}: CarriedSourceFreshnessRailProps) {
  const proofTime = formatSourceProofOccurredAt(snapshot.proofOccurredAt) ?? 'Exact proof time unavailable';
  const helper = getSourceFreshnessHelper(snapshot, decisionRoute);
  const freshnessAge = getSourceFreshnessAgeLabel(snapshot);
  const freshnessVerdict = getSourceFreshnessVerdictLabel(snapshot);

  return (
    <div data-testid={testId} className={`space-y-3 ${className}`.trim()}>
      <InlineStatus
        tone={getSourceFreshnessTone(snapshot)}
        title={title}
        message={(
          <div className="space-y-3">
            <div className="space-y-1.5">
              <p className="trust-proof trust-proof--info">
                <span className="trust-proof__label">Source</span>
                <span className="trust-proof__separator">·</span>
                <span className="trust-proof__meta">{snapshot.sourceLabel}</span>
              </p>
              <p className="trust-proof trust-proof--info">
                <span className="trust-proof__label">Owner route</span>
                <span className="trust-proof__separator">·</span>
                <span className="trust-proof__meta font-mono tabular-nums">{snapshot.ownerRoute}</span>
              </p>
              <p className="trust-proof trust-proof--info">
                <span className="trust-proof__label">Proof time</span>
                <span className="trust-proof__separator">·</span>
                <span className="trust-proof__meta font-mono tabular-nums">{proofTime}</span>
              </p>
              <p className={`trust-proof ${helper ? 'trust-proof--warning' : getSourceFreshnessTone(snapshot) === 'success' ? 'trust-proof--success' : 'trust-proof--info'}`}>
                <span className="trust-proof__label">Freshness</span>
                <span className="trust-proof__separator">·</span>
                <span className="trust-proof__meta font-mono tabular-nums">
                  {freshnessAge ? `${freshnessVerdict} · ${freshnessAge}` : freshnessVerdict}
                </span>
              </p>
              <p className={`trust-proof ${(snapshot.errorCount ?? 0) > 0 ? 'trust-proof--warning' : 'trust-proof--info'}`}>
                <span className="trust-proof__label">Imported · Skipped · Errors</span>
                <span className="trust-proof__separator">·</span>
                <span className="trust-proof__meta font-mono tabular-nums">
                  {formatCount(snapshot.importedCount)} · {formatCount(snapshot.skippedCount)} · {formatCount(snapshot.errorCount)}
                </span>
              </p>
              {snapshot.matchedCount != null ? (
                <p className={`trust-proof ${snapshot.matchedCount > 0 ? 'trust-proof--success' : 'trust-proof--info'}`}>
                  <span className="trust-proof__label">Matched rows</span>
                  <span className="trust-proof__separator">·</span>
                  <span className="trust-proof__meta font-mono tabular-nums">{formatCount(snapshot.matchedCount)}</span>
                </p>
              ) : null}
            </div>
            <p className="text-sm text-gray-300">{getSourceFreshnessSummary(snapshot, decisionRoute)}</p>
          </div>
        )}
        compact
      />
      {helper ? (
        <SetupTrustHelperBand
          tone={helper.tone}
          eyebrow={helper.eyebrow}
          message={helper.message}
          meta={helper.meta ? <span className="font-mono tabular-nums">{helper.meta}</span> : undefined}
        />
      ) : null}
    </div>
  );
}
